/**
 * W-081 — FAQ manager. Stories: US-A09 AC3, US-910.
 * Categories and Q&A pairs, bilingual, drag-to-reorder, active toggle.
 */
import { DeleteButton, List, useModalForm, useTable } from '@refinedev/antd'
import { useCan } from '@refinedev/core'
import { Button, Form, Modal, Space, Switch, Typography } from 'antd'
import { BilingualField, OrderableTable, StatusTag } from '@/components'
import { persistOrder } from '@/lib/order'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { FaqItem } from '@/types'

export const FaqManagerPage = () => {
  const translate = useAdminTranslate()
  const { tableProps, tableQueryResult } = useTable<FaqItem>({
    resource: 'faq_items',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 100 },
  })
  const { data: canEdit } = useCan({ resource: 'faq_items', action: 'edit' })

  const createModal = useModalForm<FaqItem>({
    resource: 'faq_items',
    action: 'create',
    redirect: false,
  })
  const editModal = useModalForm<FaqItem>({
    resource: 'faq_items',
    action: 'edit',
    redirect: false,
  })

  const rows = (tableProps.dataSource ?? []) as FaqItem[]

  return (
    <List
      title={translate('admin.pages.faq')}
      headerButtons={
        <Button type="primary" onClick={() => createModal.show()}>
          {translate('admin.screens.faq.addQuestion')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Typography.Text type="secondary">
          {translate('admin.screens.faq.reorderHint')}
        </Typography.Text>

        <OrderableTable<FaqItem>
          {...tableProps}
          dataSource={rows}
          reorderDisabled={!canEdit?.can}
          onReorder={ordered => {
            void persistOrder('faq_items', ordered)
              .then(() => tableQueryResult.refetch())
              .catch((error: unknown) => console.error('[faq] reorder failed', error))
          }}
          columns={[
            {
              title: translate('admin.columns.category'),
              dataIndex: 'category_fr',
              render: (value: string | null) => value ?? '—',
            },
            {
              title: translate('admin.screens.faq.questionAr'),
              dataIndex: 'question_ar',
              render: (value: string) => <span dir="rtl">{value}</span>,
            },
            { title: translate('admin.screens.faq.questionFr'), dataIndex: 'question_fr' },
            {
              title: translate('admin.common.activeFem'),
              dataIndex: 'is_active',
              render: (value: boolean) => <StatusTag kind="published" value={value} />,
            },
            {
              title: translate('admin.columns.actions'),
              dataIndex: 'id',
              render: (_: unknown, record: FaqItem) => (
                <Space>
                  <Button size="small" onClick={() => editModal.show(record.id)}>
                    {translate('admin.common.edit')}
                  </Button>
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        {...createModal.modalProps}
        title={translate('admin.screens.faq.newQuestion')}
        forceRender
      >
        <Form {...createModal.formProps} layout="vertical">
          <FaqFormFields />
        </Form>
      </Modal>

      <Modal
        {...editModal.modalProps}
        title={translate('admin.screens.faq.editQuestion')}
        forceRender
      >
        <Form {...editModal.formProps} layout="vertical">
          <FaqFormFields />
        </Form>
      </Modal>
    </List>
  )
}

const FaqFormFields = () => {
  const translate = useAdminTranslate()

  return (
    <>
      <BilingualField name="category" label={translate('admin.columns.category')} maxLength={60} />
      <BilingualField
        name="question"
        label={translate('admin.screens.faq.question')}
        requiredAr
        maxLength={200}
      />
      <BilingualField
        name="answer"
        label={translate('admin.screens.faq.answer')}
        multiline
        requiredAr
        maxLength={2000}
      />
      <Form.Item
        name="is_active"
        label={translate('admin.common.activeFem')}
        valuePropName="checked"
        initialValue
      >
        <Switch />
      </Form.Item>
    </>
  )
}
